import { useMemo } from "react";
import { CalendarEvent, EVENT_TYPE_CONFIG } from "@/hooks/useCalendarEvents";
import { cn } from "@/lib/utils";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { EventHoverActions } from "./EventHoverActions";
import { useIsMobile } from "@/hooks/use-mobile";
import { Plus } from "lucide-react";

interface Props {
  month: Date;
  events: CalendarEvent[];
  layers: Record<string, boolean>;
  selectedDate: Date;
  onDayClick: (d: Date) => void;
  onEmptyCellClick?: (d: Date) => void;
  onEventPreview?: (ev: CalendarEvent) => void;
  onEventEdit?: (ev: CalendarEvent) => void;
  highlightEventId?: string | null;
}

function getMonthGrid(month: Date) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const first = new Date(year, m, 1);
  const last = new Date(year, m + 1, 0);
  const startPad = first.getDay();
  const totalDays = last.getDate();

  const weeks: (Date | null)[][] = [];
  let week: (Date | null)[] = [];
  for (let i = 0; i < startPad; i++) week.push(null);
  for (let d = 1; d <= totalDays; d++) {
    week.push(new Date(year, m, d));
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }
  return weeks;
}

function dateKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const DAY_LABELS_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_LABELS_SHORT = ["S", "M", "T", "W", "T", "F", "S"];

export const CalendarMonthView = ({
  month, events, layers, selectedDate, onDayClick,
  onEmptyCellClick, onEventPreview, onEventEdit, highlightEventId,
}: Props) => {
  const isMobile = useIsMobile();
  const weeks = useMemo(() => getMonthGrid(month), [month]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    events
      .filter((e) => layers[e.event_type] !== false)
      .forEach((e) => {
        const start = new Date(e.start_date + "T00:00:00");
        const end = e.end_date ? new Date(e.end_date + "T00:00:00") : start;
        const cur = new Date(start);
        while (cur <= end) {
          const k = dateKey(cur);
          if (!map[k]) map[k] = [];
          map[k].push(e);
          cur.setDate(cur.getDate() + 1);
        }
      });
    return map;
  }, [events, layers]);

  const MAX_PILLS = isMobile ? 2 : 3;
  const dayLabels = isMobile ? DAY_LABELS_SHORT : DAY_LABELS_FULL;

  return (
    <div className="flex-1 overflow-auto">
      <div className="grid grid-cols-7 border-b">
        {dayLabels.map((d, i) => (
          <div key={`${d}-${i}`} className="px-1 md:px-2 py-1 md:py-1.5 text-[9px] md:text-[10px] font-semibold text-muted-foreground text-center uppercase">
            {d}
          </div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b min-h-[60px] md:min-h-[100px]">
          {week.map((day, di) => {
            if (!day) {
              return <div key={`empty-${di}`} className="border-r bg-muted/30" />;
            }
            const k = dateKey(day);
            const dayEvents = eventsByDate[k] || [];
            const isEmpty = dayEvents.length === 0;
            const isToday =
              day.getFullYear() === today.getFullYear() &&
              day.getMonth() === today.getMonth() &&
              day.getDate() === today.getDate();
            const isWeekend = di === 0 || di === 6;

            return (
              <div
                key={k}
                className={cn(
                  "border-r p-0.5 md:p-1 cursor-pointer transition-colors group relative",
                  isWeekend && "bg-muted/20",
                  isEmpty ? "hover:bg-primary/5" : "hover:bg-muted/40"
                )}
                onClick={() => {
                  if (isEmpty && onEmptyCellClick) {
                    onEmptyCellClick(day);
                  } else {
                    onDayClick(day);
                  }
                }}
              >
                <div className="flex justify-between items-start">
                  <span
                    className={cn(
                      "text-[10px] md:text-xs h-4 w-4 md:h-5 md:w-5 flex items-center justify-center rounded-full",
                      isToday && "bg-primary text-primary-foreground font-bold"
                    )}
                  >
                    {day.getDate()}
                  </span>
                  {isEmpty && (
                    <Plus className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </div>
                <div className="mt-0.5 space-y-0.5">
                  {dayEvents.slice(0, MAX_PILLS).map((ev) => {
                    const cfg = EVENT_TYPE_CONFIG[ev.event_type];
                    return (
                      <HoverCard key={ev.id} openDelay={300} closeDelay={150}>
                        <HoverCardTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className={cn(
                              "w-full text-left text-[8px] md:text-[10px] leading-tight px-0.5 md:px-1 py-0.5 rounded truncate border-l-2",
                              cfg.bgClass, cfg.textClass, cfg.borderClass,
                              highlightEventId === ev.id && "ring-2 ring-primary ring-offset-1 animate-pulse"
                            )}
                          >
                            {ev.title}
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-auto p-0" align="start" side="right">
                          <EventHoverActions
                            event={ev}
                            onPreview={(e) => onEventPreview?.(e)}
                            onEdit={(e) => onEventEdit?.(e)}
                          />
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })}
                  {dayEvents.length > MAX_PILLS && (
                    <span className="text-[8px] md:text-[9px] text-muted-foreground pl-0.5 md:pl-1">
                      +{dayEvents.length - MAX_PILLS} more
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
